import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

interface WhatsAppLinkProps {
  /** Customer phone in any format. Non-digits are stripped. */
  phone?: string | null;
  /** Optional pre-filled message body. */
  message?: string;
  /** Defaults to '91' (India). Used when the phone doesn't start with a country code. */
  defaultCountryCode?: string;
  /** Tooltip override; defaults to "Send WhatsApp". */
  tooltip?: string;
  size?: 'small' | 'medium';
  /** If true, render an inline anchor with text label instead of an icon button. */
  inline?: boolean;
}

/** Strip non-digits and prepend the default country code if missing. */
export function toWhatsAppDigits(phone: string, defaultCountryCode = '91'): string {
  const digitsOnly = phone.replace(/\D/g, '');
  if (!digitsOnly) return '';
  // 10-digit local number → prepend country code.
  if (digitsOnly.length === 10) return defaultCountryCode + digitsOnly;
  // Already includes country code or longer → use as-is.
  return digitsOnly;
}

/** Build a wa.me deep link. Returns empty string if the phone can't be parsed. */
export function buildWhatsAppHref(phone: string, message?: string, defaultCountryCode = '91'): string {
  const digits = toWhatsAppDigits(phone, defaultCountryCode);
  if (!digits) return '';
  const base = `https://wa.me/${digits}`;
  if (!message) return base;
  return `${base}?text=${encodeURIComponent(message)}`;
}

const WhatsAppLink: React.FC<WhatsAppLinkProps> = ({
  phone,
  message,
  defaultCountryCode = '91',
  tooltip = 'Send WhatsApp',
  size = 'small',
  inline = false,
}) => {
  if (!phone) return null;
  const href = buildWhatsAppHref(phone, message, defaultCountryCode);
  if (!href) return null;

  if (inline) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#25D366',
          fontWeight: 600,
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <WhatsAppIcon sx={{ fontSize: 16 }} />
        WhatsApp
      </a>
    );
  }

  return (
    <Tooltip title={tooltip} arrow>
      <IconButton
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        size={size}
        sx={{
          color: '#25D366',
          '&:hover': { bgcolor: 'rgba(37, 211, 102, 0.12)' },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <WhatsAppIcon fontSize={size === 'small' ? 'small' : 'medium'} />
      </IconButton>
    </Tooltip>
  );
};

export default WhatsAppLink;
